/**
 * @module app.cd
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode } from 'aurelia-framework';
import { DocumentsService } from 'app/core/services/documents-service';
import { IDocumentsConfig, INotesConfig } from 'app/core/models/essette-interfaces';
import { modelCodeToResourceKey } from 'app/shared/lib/i18n';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/essette-api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { DownloadManager } from 'app/shared/lib/download/manager';
import { openNotes } from 'app/core/components/notes/notes-grid';
import { IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { getDocumentIcon } from 'app/shared/lib/utilities';
import * as CM from 'app/cm/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

type ShowLinks = {
    appCode: string,
    hasDownloadDocuments: boolean,
    showSummaryLink: boolean,
    openSummary?: (id: string, number: string) => void,
    transitionalSummaryId?: number
}

interface CustomMemberRollupDocument extends CD.MemberRollupDocument {
    notesMember360?: Core.Note[]
}

const NOTE_FLAG_CODES = ['CD.MemberHistory'];

@autoinject()
export class MemberDocuments extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId!: string;

    public documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'cd/members',
        targetType: 'CD.Member',
        addTargetProperty: (document: CD.MemberRollupDocument) => { document.member = { id: this.memberId } },
        hasTasks: false
    };

    protected memberDocuments: ReadonlyArray<CustomMemberRollupDocument> = [];

    protected openNotes = openNotes;

    protected getDocumentIcon = getDocumentIcon;

    protected faxNotesConfig: INotesConfig = {
        serviceRootUrl: 'fm/faxes',
        targetType: 'FM.FaxDocument',
        moduleCode: 'FM',
        excludeSystem: false,
        flagCodes: NOTE_FLAG_CODES
    };

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    private showLinks: ReadonlyArray<ShowLinks> = [];
    protected showNotesPopup: boolean = false;
    protected hasAuthRoute = false;
    protected hasCaseRoute = false;

    private openCaseSummary = openCaseSummary;
    private openAuthSummary = openAuthSummary;
    protected modelCodeToResourceKey = modelCodeToResourceKey;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: DocumentsService,
        private readonly downloadManager: DownloadManager,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'metadata.created.timestamp',
                direction: SortDirection.Descending
            }
        ];
    }
    protected bind(): void {
        this.service.configure(this.documentsConfig);
        this.downloadManager.configureService(this.documentsConfig);

        this.reload();
    }

    ////****************** Events ***************************************************////

    public async reload(): Promise<void> {
        await this.loadMemberDocuments();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'metadata.created.timestamp',
                        direction: SortDirection.Descending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected handleDownloadFile(event: MouseEvent, document: Core.Document): void {
        this.downloadManager.configureService(this.documentsConfig);
        this.downloadManager.handleDownloadFile(event, document, true);
    }

    protected shouldView(document: Core.Document): void {
        this.downloadManager.shouldView(document);
    }

    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');

        this.showLinks = [
            {
                appCode: 'CS.MemberIssue',
                hasDownloadDocuments: true,//this.accountContext.hasPermission('CS.IssueDocument.Download'),
                showSummaryLink: this.accountContext.hasRouteAccess('2007'),//'cs.issue.summary'
                transitionalSummaryId: 2007
            },
            {
                appCode: 'CM.Case',
                hasDownloadDocuments: true,//this.accountContext.hasPermission('CM.CaseDocument.Download'),
                showSummaryLink: this.accountContext.hasRouteAccess('cm.case.summary'),
                openSummary: (id: string, number: string) => {
                    const c = { id, number, member: { id: this.memberId } } as CM.Case;
                    this.openCaseSummary(c, this.hasCaseRoute, true);
                }
            },
            {
                appCode: 'UM.Auth',
                hasDownloadDocuments: true,//this.accountContext.hasPermission('UM.AuthDocument.Download'),
                showSummaryLink: this.accountContext.hasRouteAccess('um.auth.summary'),
                openSummary: (id: string, number: string) => {
                    const c = { id, number, member: { id: this.memberId } } as UM.Auth;
                    this.openAuthSummary(c, this.hasAuthRoute, true);
                }
            },
            {
                appCode: 'AG.Case',
                hasDownloadDocuments: true,//this.accountContext.hasPermission('AG.CaseDocument.Download'),
                showSummaryLink: this.accountContext.hasRouteAccess('6004'),//'ag.case.summary'
                transitionalSummaryId: 6004
            },
            {
                appCode: 'PM.Candidate',
                hasDownloadDocuments: true,//this.accountContext.hasPermission('PM.CandidateDocument.Download'),
                showSummaryLink: this.accountContext.hasRouteAccess('14007'),//'pm.candidate.summary'
                transitionalSummaryId: 14007
            },
            {
                appCode: 'PM.Member',
                hasDownloadDocuments: true,//this.accountContext.hasPermission('PM.MemberDocument.Download'),
                showSummaryLink: false
            }
        ];

        this.showNotesPopup = this.accountContext.hasRouteAccess('fm.queues.fax.notes');
    }

    protected hasDownloadDocuments(code: string): boolean {
        const applicationLink = this.showLinks.find(f => f.appCode === code);
        if(!applicationLink) return false;
        return applicationLink.hasDownloadDocuments;
    }
    protected showSummaryLink(code: string): boolean {
        const applicationLink = this.showLinks.find(f => f.appCode === code);
        if(!applicationLink) return false;
        return applicationLink.showSummaryLink;
    }
    protected getTransitionalSummaryId(code: string): number | undefined {
        const applicationLink = this.showLinks.find(f => f.appCode === code);
        if(!applicationLink || !applicationLink.transitionalSummaryId) return undefined;
        return applicationLink.transitionalSummaryId;
    }
    protected openSummary(target: CD.MemberRollupDocumentTargetModel) {
        if(!target.type?.id || !target.id || !target.description) return;
        const targetTypeCode = target.type.code;
        const applicationLink = this.showLinks.find(f => f.appCode === targetTypeCode);
        if(!applicationLink) return;
        applicationLink.openSummary!(target.id, target.description);
    }

    protected openNotePopup(document: Core.Document): void {
        if(!document.id) {
            return;
        }

        const dialogOptions: IDialogOptions = {
            width: '10',
            height: '11',
            helpTopic: 'um.auth.document-fax-notes',
            title: {
                key: 'fm:routes.fax-notes.$title'
            }
        };

        this.openNotes(document.id, this.faxNotesConfig, dialogOptions, false, false, false, false, true);
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberDocuments(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes: string[] = [
            'category',
            'source',
            'mediaType',
            'attachedTo',
            'attachedTo.metadata.attached.byUser',
            'attachedTo.type',
            'notes',
        ];

        const fields: string[] = [
            'attachedTo.description',
            'attachedTo.metadata.attached.timestamp',
            'attachedTo.metadata.attached.byUser.name.full',
            'attachedTo.type.code',
            'category.description',
            'description',
            'file.name',
            'file.size',
            'isFax',
            'mediaType.code',
            'source.description',
            'summary',
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadDocumentsByTarget(this.memberId + '/rollup', options, includes, fields);
            this.memberDocuments = response.results as ReadonlyArray<CD.MemberRollupDocument>;
            // Filter for Notes that can be displayed on Member 360
            this.memberDocuments.forEach(d => {
                d.notesMember360 = d.notes?.filter(n => n.flags?.find(f => NOTE_FLAG_CODES.indexOf(f.code ?? "") > -1))
            });
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
